import Link from '@/routing/AppLink';
import { useRouter } from '@/routing/useAppRouter';
import _isNumber from 'lodash/isNumber';
import React from 'react';
import { useForm } from 'react-hook-form';

import useModal from '@/hooks/shared/useModal';

import dateFormat from '@/utils/dateFormat';
import { buildNewsArticlePath } from '@/utils/newsUrl';

import NewsPreviewMedia from '@/components/apps/pages/about/news/NewsPreviewMedia';
import {
  IconArrowUpRightFromSquareRound,
  IconLockRoundLine,
  IconPaperclipRound,
} from '@/components/apps/ui/icon';
import Overview from '@/components/apps/ui/overview/Overview';
import Badge from '@/components/shared/ui/badge/Badge';
import { InputBoxWithHookForm } from '@/components/shared/ui/forms/inputs';
import { CustomModal } from '@/components/shared/ui/modals';

import theme from '@/styles/theme';

import type { NewsListItemProps } from './NewsListItem.types';

type FormType = {
  password: string;
};

export default function NewsListItem({
  url,
  bcIdx,
  status,
  title,
  type,
  writer,
  commentCount,
  viewCount,
  date,
  hasFile,
  password,
  showBadge = false,
  showName = true,
  showCommentCount = true,
  showViewCount = true,
  showDate = true,
  imgUrl,
  videoUrl,
}: NewsListItemProps) {
  const router = useRouter();
  const detailUrl = buildNewsArticlePath({
    idx: bcIdx,
    title,
  });
  const hasHref = url?.link_status === '1';
  const isTargetBlank = url?.link_target === '1'; // 링크 게시글 새 창 열기 여부
  const targetUrl = hasHref ? url.link : detailUrl;
  const isNotice = type === 1;
  const isPrivate = type === 3;

  const { showCustomModal } = useModal();

  const { register, handleSubmit, resetField } = useForm<FormType>({
    mode: 'all',
    defaultValues: {
      password: '',
    },
  });

  // TODO : 관련 기능 및 디자인 명세 필요
  // 모달을 통해 비밀번호를 확인하고 페이지를 이동하는 함수
  const handlePasswordCheck = () => {
    showCustomModal({
      component: CustomModal,
      size: 'sm',
      title: '비밀번호를 입력해 주세요',
      content: (
        <div>
          <InputBoxWithHookForm
            {...register('password', { required: true })}
            name="password"
            type="password"
            autoComplete="one-time-code"
            autoSave="off"
            placeholder="비밀번호"
          />
          <div className="text-xs text-[red]">
            * 비밀글 관련 기능/디자인 명세 필요
          </div>
          <div className="text-xs text-[red]">
            * 현재는 따로 검증 로직이 없습니다
          </div>
        </div>
      ),
      closeOnClickOutside: true,
      onSubmit: handleSubmit(onModalSubmit),
    });
  };

  // URL 이동 처리 함수
  const navigateToUrl = (url: string, newTab: boolean) => {
    if (newTab) {
      window.open(url, '_blank');
    } else {
      router.push(url);
    }
  };

  // 모달에서 제출된 패스워드를 처리하는 함수
  const onModalSubmit = (data: FormType) => {
    // TODO : 비밀번호 확인 API 필요
    console.log({ data });
    // if (data.password === password) {
    //   navigateToUrl(targetUrl, isTargetBlank);
    // } else {
    //   alert('비밀번호가 틀렸습니다.');
    // }
    navigateToUrl(targetUrl, isTargetBlank);
    resetField('password');
  };

  // 클릭 핸들러
  const handleOnClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!password) {
      return;
    }

    e.preventDefault();
    handlePasswordCheck();
  };

  return (
    <Link
      href={targetUrl}
      target={isTargetBlank ? '_blank' : undefined}
      rel={isTargetBlank ? 'noopener noreferrer' : undefined}
      onClick={handleOnClick}
      className="grid min-h-32 grid-cols-[112px_minmax(0,1fr)] gap-4 border-b border-apps-coreProductDivider py-4 text-inherit no-underline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-apps-coreGrayBlack lg:flex lg:min-h-20 lg:flex-row lg:items-center lg:gap-6"
    >
      <div className="relative aspect-[112/76] w-full overflow-hidden rounded-xl border border-apps-coreProductDivider lg:aspect-[158/88] lg:w-[158px]">
        <NewsPreviewMedia
          posterUrl={imgUrl}
          videoUrl={videoUrl}
          alt={title}
          sizes="(min-width: 992px) 158px, 112px"
        />
      </div>
      <div className="flex min-w-0 flex-col gap-2 lg:w-[calc(100%-182px)] lg:flex-row lg:gap-0">
        <div className="flex max-w-full flex-col items-start gap-2 lg:flex-row lg:items-center lg:gap-3">
          {showBadge && (
            <>
              {isNotice && (
                <Badge
                  color="primary"
                  variant="fill"
                  emphasis="heavy"
                  shape="box"
                  size="lg"
                >
                  공지
                </Badge>
              )}

              {/* TODO : 관련 기능 및 디자인 명세 필요 */}
              {(status === 3 || status === 4) && ( // 답변대기(3) 혹은 답변완료(4) status일때만 뱃지 표시
                <Badge color={status === 3 ? 'gray' : 'primary'}>
                  {status === 3 ? '답변대기' : '답변완료'}
                </Badge>
              )}
            </>
          )}

          <div className="flex w-full items-center gap-3 lg:flex-col lg:items-start lg:gap-2">
            <span className="line-clamp-2 block w-full text-lg font-medium leading-[145%] tracking-[-0.36px] text-apps-coreGrayBlack lg:self-stretch lg:text-lg lg:leading-[160%]">
              {title}
            </span>

            <div className="inline-flex items-center gap-2">
              {isPrivate && (
                <IconLockRoundLine
                  size={20}
                  color={theme`colors.apps.coreGrayBlack`}
                />
              )}
              {hasFile && (
                <IconPaperclipRound
                  size={20}
                  color={theme`colors.apps.coreGrayBlack`}
                />
              )}
              {hasHref && (
                <IconArrowUpRightFromSquareRound
                  size={20}
                  color={theme`colors.apps.coreGrayBlack`}
                />
              )}
            </div>
          </div>
        </div>

        <div className="mt-auto flex flex-none items-center gap-5 pt-1 lg:ml-auto lg:pt-0 lg:pl-10">
          {showName && writer && (
            <p className="text-[15px] leading-[140%] tracking-[-0.3px] text-apps-coreGray02">
              {writer}
            </p>
          )}

          {showCommentCount && _isNumber(commentCount) && (
            <Overview color="gray" icon="talkbox">
              {commentCount.toLocaleString()}
            </Overview>
          )}

          {showViewCount && _isNumber(viewCount) && (
            <Overview color="gray" icon="eye">
              {viewCount.toLocaleString()}
            </Overview>
          )}

          {showDate && (
            <p className="text-[15px] font-normal leading-[140%] tracking-[-0.3px] text-apps-coreGray02">
              {dateFormat(date, 'YYYY-MM-DD')}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
}
